import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchWithdrawAccountList,
  setDefaultWithdrawAccount,
  getWalletDetail,
  applyWithdraw,
  type WithdrawAccount,
  type WalletInfo,
  type WalletLogItem,
} from "@/api/wallet";
import BindAccountDrawer from "@/components/BindAccountDrawer";

/** 主题色 */
const THEME = {
  primary: "#FF7043",
  primaryLight: "#FFF0EB",
};

export default function WalletPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"全部" | "收入" | "支出">("全部");
  const [showWithdrawDrawer, setShowWithdrawDrawer] = useState(false);
  const [showBindAccountDrawer, setShowBindAccountDrawer] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);

  // 提现账户（来自API）
  const [accounts, setAccounts] = useState<WithdrawAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);

  // 钱包数据（来自API）
  const [walletInfo, setWalletInfo] = useState<WalletInfo | null>(null);
  const [logs, setLogs] = useState<WalletLogItem[]>([]);
  const [walletLoading, setWalletLoading] = useState(true);

  /** 获取钱包详情（余额 + 明细） */
  const fetchWalletData = useCallback(async (logType?: "全部" | "收入" | "支出") => {
    try {
      setWalletLoading(true);
      const logTypeMap: Record<string, 0 | 1 | 2> = { 全部: 0, 收入: 1, 支出: 2 };
      const data = await getWalletDetail({
        limit: 20,
        log_type: logTypeMap[logType ?? filter] ?? 0,
        before_ts: 0,
      });
      setWalletInfo(data.wallet);
      setLogs(data.logs.list || []);
    } catch (e) {
      console.error("[wallet] 获取钱包详情失败:", e);
    } finally {
      setWalletLoading(false);
    }
  }, [filter]);

  /** 获取提现账户列表 */
  const fetchAccounts = useCallback(async () => {
    try {
      setAccountsLoading(true);
      const list = await fetchWithdrawAccountList();
      setAccounts(list);
      // 默认选中第一个默认账户
      if (list.length > 0 && !selectedAccountId) {
        const defaultAcc = list.find((a) => a.is_default === 1) || list[0];
        setSelectedAccountId(defaultAcc.id);
      }
    } catch (e) {
      console.error("[wallet] 获取账户列表失败:", e);
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWalletData();
    fetchAccounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 切换筛选时重新请求明细 */
  useEffect(() => {
    if (!walletLoading) {
      fetchWalletData(filter);
    }
  }, [filter]);

  /** 设置默认账户 */
  const handleSetDefault = async (id: number) => {
    try {
      await setDefaultWithdrawAccount(id);
      setSelectedAccountId(id);
      // 刷新列表
      await fetchAccounts();
    } catch (e) {
      console.error("[wallet] 设置默认失败:", e);
    }
  };

  /** 脱敏卡号显示尾号 */
  const getCardTail = (masked: string): string => {
    // masked 格式如 "**** **** **** 1234"
    return masked.replace(/\*/g, "").replace(/\s/g, "") || masked;
  };

  return (
    <div className="min-h-screen pb-16" style={{ background: "#F5F6F8" }}>
      {/* 顶部导航 + 余额区域 */}
      <div
        className="px-5 pb-7"
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)",
          background: "linear-gradient(160deg, rgb(255, 107, 53) 0%, rgb(255, 154, 92) 100%",
        }}
      >
        {/* 导航栏 */}
        <div className="flex items-center gap-3 mb-7">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:opacity-70 transition-opacity"
            style={{ background: "rgba(255,255,255,0.25)" }}
          >
            <i className="ri-arrow-left-line text-white text-lg" />
          </button>
          <h1 className="text-[17px] font-semibold text-white">个人钱包</h1>
        </div>

        {/* 余额信息 */}
        <div className="text-center mb-6">
          <div className="text-white/80 text-sm mb-1 tracking-wide">可用余额</div>
          {walletLoading ? (
            <div className="text-white font-bold leading-tight" style={{ fontSize: 42 }}>...</div>
          ) : (
            <>
              <div className="text-white font-bold leading-tight" style={{ fontSize: 42 }}>
                ¥{walletInfo?.usable_balance?.toFixed(2) ?? "0.00"}
              </div>
              <div className="text-white/60 text-xs mt-2.5">
                冻结押金：¥{walletInfo?.deposit_amount ?? 0}
              </div>
            </>
          )}
        </div>

        {/* 申请提现按钮 */}
        <div className="flex justify-center">
          <button
            onClick={() => setShowWithdrawDrawer(true)}
            disabled={accounts.length === 0}
            className="px-12 py-2.5 rounded-full font-medium text-sm cursor-pointer active:scale-[0.97] transition-all flex items-center justify-center gap-1.5"
            style={{
              background: "#FFFFFF",
              color: "#FF7043",
              opacity: accounts.length === 0 ? 0.5 : 1,
            }}
          >
            <i className="ri-file-list-3-line text-base" />
            申请提现
          </button>
        </div>
      </div>

      {/* 提现账户区域 */}
      <div className="px-4 mt-6">
        <div
          className="bg-white rounded-2xl p-4"
          style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}
        >
          {/* 标题行 */}
          <div className="flex items-center justify-between mb-3.5">
            <div className="flex items-center gap-1.5">
              <i className="ri-wallet-3-line text-base" style={{ color: THEME.primary }} />
              <span className="text-[15px] font-medium" style={{ color: "#333333" }}>
                提现账户
              </span>
            </div>
            <button
              onClick={() => setShowBindAccountDrawer(true)}
              className="flex items-center gap-0.5 text-sm cursor-pointer active:opacity-60 transition-opacity"
              style={{ color: THEME.primary, background: "none", border: "none" }}
            >
              <span style={{ fontSize: 15 }}>+</span>
              <span>绑定新账户</span>
            </button>
          </div>

          {/* 账户列表 */}
          {accountsLoading ? (
            <div className="text-center py-6 text-sm" style={{ color: "#BBBBBB" }}>加载中...</div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-8">
              <i className="ri-bank-card-line text-4xl" style={{ color: "#DDDDDD" }} />
              <p className="mt-2 text-sm" style={{ color: "#AAAAAA" }}>暂无提现账户，请先绑定</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {accounts.map((acc) => (
                <div
                  key={acc.id}
                  className="flex items-center justify-between px-3.5 py-3 rounded-xl cursor-pointer active:bg-gray-50 transition-colors"
                  style={{ background: "#FAFAFA" }}
                  onClick={() => handleSetDefault(acc.id)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ background: "#FFF3E0" }}
                    >
                      <i className="ri-bank-line text-lg" style={{ color: THEME.primary }} />
                    </div>
                    <div>
                      <div className="text-sm font-medium" style={{ color: "#333333" }}>
                        {acc.bank_name}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: "#999999" }}>
                        {acc.cardholder_name} &middot; 尾号 {getCardTail(acc.card_number_masked)}
                      </div>
                    </div>
                  </div>
                  {acc.is_default === 1 && (
                    <span
                      className="text-xs px-2 py-0.5 rounded"
                      style={{ background: THEME.primaryLight, color: THEME.primary }}
                    >
                      默认
                    </span>
                  )}
                  {selectedAccountId === acc.id && acc.is_default !== 1 && (
                    <span
                      className="text-xs px-2 py-0.5 rounded"
                      style={{ background: "#E8E8E8", color: "#888888" }}
                    >
                      已选
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 账单明细 */}
      <div className="px-4 mt-4">
        <div
          className="bg-white rounded-2xl p-4"
          style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}
        >
          {/* 标题 + 筛选 */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-[15px] font-medium" style={{ color: "#333333" }}>账单明细</span>
            <div className="flex gap-1">
              {(["全部", "收入", "支出"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className="px-3 py-1 rounded-full text-xs cursor-pointer transition-all active:scale-95"
                  style={
                    filter === f
                      ? { background: "#FFFFFF", color: THEME.primary, border: `1px solid ${THEME.primary}` }
                      : { background: "#F5F5F5", color: "#888888", border: "1px solid transparent" }
                  }
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* 账单列表 */}
          {walletLoading ? (
            <div className="text-center py-10 text-sm" style={{ color: "#BBBBBB" }}>加载中...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-10">
              <i className="ri-file-list-3-line text-4xl" style={{ color: "#EEEEEE" }} />
              <p className="mt-2 text-sm" style={{ color: "#BBBBBB" }}>暂无账单记录</p>
            </div>
          ) : (
            <div className="space-y-0 divide-y" style={{ borderColor: "#F5F5F5" }}>
              {logs.map((log) => (
                <div key={log.id} className="flex items-center justify-between py-3.5">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{
                        background: log.type === "收入" ? "#E8F5E9" : "#FFEBEE",
                      }}
                    >
                      <i
                        className={`text-base ${log.type === "收入" ? "ri-add-line" : "ri-subtract-line"}`}
                        style={{ color: log.type === "收入" ? "#4CAF50" : "#FF7043" }}
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium" style={{ color: "#333333" }}>
                        {log.detail_type}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: "#AAAAAA" }}>
                        {log.create_time}
                      </div>
                    </div>
                  </div>
                  <span
                    className="text-sm font-semibold"
                    style={{
                      color: log.type === "收入" ? "#4CAF50" : "#333333",
                    }}
                  >
                    {log.type === "收入" ? "+" : "-"}¥{log.amount.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ========== 提现抽屉 ========== */}
      {showWithdrawDrawer && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[100]"
            onClick={() => setShowWithdrawDrawer(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-[101] rounded-t-2xl bg-white animate-slide-up px-5 pt-4 pb-8"
            style={{ maxHeight: "85vh", overflowY: "auto", paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}
          >
            {/* 标题栏 */}
            <div className="flex items-center justify-between mb-5 relative">
              <button
                onClick={() => setShowWithdrawDrawer(false)}
                className="w-8 h-8 flex items-center justify-center cursor-pointer active:opacity-60"
              >
                <i className="ri-close-line text-xl" style={{ color: "#666666" }} />
              </button>
              <span className="text-[17px] font-semibold absolute left-1/2 -translate-x-1/2" style={{ color: "#333333" }}>
                申请提现
              </span>
              <div className="w-8" />
            </div>

            {/* 可用余额 */}
            <div
              className="rounded-xl px-4 py-4 mb-5"
              style={{ background: "#FFF7F2" }}
            >
              <div className="text-xs mb-1" style={{ color: "#999999" }}>可提现余额</div>
              <div className="text-[28px] font-bold" style={{ color: THEME.primary }}>
                ¥{walletInfo?.usable_balance?.toFixed(2) ?? "0.00"}
              </div>
            </div>

            {/* 提现金额 */}
            <div className="mb-4">
              <div className="text-[13px] mb-2" style={{ color: "#666666" }}>提现金额</div>
              <div
                className="flex items-center px-4 py-3 rounded-xl"
                style={{ background: "#F5F5F5", border: "1px solid #EEEEEE" }}
              >
                <span className="text-lg mr-1.5" style={{ color: "#333333" }}>¥</span>
                <input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="0.00"
                  className="flex-1 text-lg outline-none placeholder:text-gray-400"
                  style={{ background: "transparent", color: "#333333" }}
                />
              </div>
            </div>

            {/* 提现到 */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[13px]" style={{ color: "#666666" }}>提现到</span>
                <button
                  onClick={() => { setShowWithdrawDrawer(false); setTimeout(() => setShowBindAccountDrawer(true), 300); }}
                  className="flex items-center gap-0.5 cursor-pointer"
                  style={{ color: THEME.primary, background: "none", border: "none", fontSize: 13 }}
                >
                  <span>+</span>
                  <span>绑定新账户</span>
                </button>
              </div>

              {/* 账户选择列表 - 来自真实数据 */}
              {accounts.length === 0 ? (
                <div className="text-center py-6 text-sm" style={{ color: "#AAAAAA" }}>
                  请先绑定提现账户
                </div>
              ) : (
                <div className="space-y-2.5">
                  {accounts.map((acc) => (
                    <div
                      key={acc.id}
                      onClick={() => setSelectedAccountId(acc.id)}
                      className="flex items-center justify-between px-3.5 py-3 rounded-xl cursor-pointer active:bg-gray-50 transition-colors"
                      style={{
                        background: selectedAccountId === acc.id ? "#FFF7F2" : "#FAFAFA",
                        border: selectedAccountId === acc.id ? "1px solid #FFE0D0" : "1px solid transparent",
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center"
                          style={{ background: "#FFF3E0" }}
                        >
                          <i className="ri-bank-line text-base" style={{ color: THEME.primary }} />
                        </div>
                        <div>
                          <div className="text-sm font-medium" style={{ color: "#333333" }}>{acc.bank_name}</div>
                          <div className="text-xs mt-0.5" style={{ color: "#AAAAAA" }}>
                            {acc.cardholder_name} &middot; 尾号 {getCardTail(acc.card_number_masked)}
                          </div>
                        </div>
                      </div>
                      {selectedAccountId === acc.id && (
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ background: THEME.primary }}
                        >
                          <i className="ri-check-line text-white text-xs" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 手续费提示 */}
            <div className="flex items-start gap-1.5 my-4">
              <i className="ri-information-line text-xs mt-0.5" style={{ color: "#BBBBBB" }} />
              <span className="text-xs" style={{ color: "#BBBBBB" }}>
                提现手续费：<span style={{ color: THEME.primary }}>¥0（免费）</span>，预计 1 个工作日内到账
              </span>
            </div>

            {/* 确认提现按钮 */}
            <button
              className="w-full py-3.5 rounded-full text-white font-medium text-base cursor-pointer active:opacity-80 transition-opacity disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #FFAB91, #FF7043)" }}
              disabled={!withdrawAmount || !selectedAccountId || withdrawing}
              onClick={async () => {
                if (!selectedAccountId) return;
                const amount = Number(withdrawAmount);
                if (!amount || amount <= 0) { alert("请输入有效金额"); return; }
                if (walletInfo && amount > walletInfo.usable_balance) {
                  alert("提现金额不能超过可提现余额");
                  return;
                }
                try {
                  setWithdrawing(true);
                  await applyWithdraw(amount, selectedAccountId);
                  // 成功后关闭抽屉并刷新数据
                  setShowWithdrawDrawer(false);
                  setWithdrawAmount("");
                  fetchWalletData();
                  alert("提现申请已提交，等待审核");
                } catch (e: any) {
                  console.error("[wallet] 提现失败:", e);
                  alert(e?.message || "提现失败，请稍后重试");
                } finally {
                  setWithdrawing(false);
                }
              }}
            >
              {withdrawing ? "提交中..." : "确认提现"}
            </button>
          </div>
        </>
      )}

      {/* ========== 绑定提现账户抽屉 ========== */}
      <BindAccountDrawer
        visible={showBindAccountDrawer}
        onClose={() => {
          setShowBindAccountDrawer(false);
          // 绑定成功后刷新账户列表
          fetchAccounts();
        }}
      />
    </div>
  );
}
